import { useEffect, useRef, useState } from "react";
import { exclusionSummaryError, loadExclusionSummary, prioritizeExclusionReasons } from "../api/exclusionSummary";
import type { ExclusionSummary, ExclusionSummarySource } from "../types";

export function useExclusionSummary(source: ExclusionSummarySource, enabled: boolean, refreshKey?: unknown, metricId?: string, page = 1) {
    const { reportId, configVersionId, artifactId } = source;
    const [attempt, setAttempt] = useState(0);
    const [state, setState] = useState<{ data: ExclusionSummary | null; loading: boolean; error: string }>({ data: null, loading: false, error: "" });
    const cached = useRef<{ key: string; refreshKey: unknown; pages: Map<number, ExclusionSummary> } | null>(null);
    const key = JSON.stringify([reportId, configVersionId, artifactId, metricId, attempt]);
    useEffect(() => {
        if (cached.current?.key !== key || cached.current.refreshKey !== refreshKey) {
            cached.current = { key, refreshKey, pages: new Map() };
        }
        const cachedPage = cached.current.pages.get(page);
        if (cachedPage) {
            cached.current.pages.delete(page);
            cached.current.pages.set(page, cachedPage);
            setState({ data: cachedPage, loading: false, error: "" });
            return;
        }
        setState({ data: null, loading: false, error: "" });
        if (!enabled || !reportId) return;
        const controller = new AbortController();
        setState({ data: null, loading: true, error: "" });
        // Strict Mode replays setup/cleanup synchronously in development. Defer
        // dispatch one microtask so that replay never sends a request to abort.
        Promise.resolve().then(async () => {
            if (controller.signal.aborted) return;
            const data = await loadExclusionSummary({ reportId, configVersionId, artifactId }, controller.signal, metricId, page, page > 1 || attempt > 0);
            if (controller.signal.aborted) return;
            cached.current!.pages.set(page, data);
            // Retain the prefetched first page and four recently visited pages.
            if (cached.current!.pages.size > 5) {
                const oldestPage = Array.from(cached.current!.pages.keys()).find(value => value !== 1 && value !== page);
                if (oldestPage != null) cached.current!.pages.delete(oldestPage);
            }
            setState({ data, loading: false, error: "" });
        }).catch(error => {
            if (!controller.signal.aborted) setState({ data: null, loading: false, error: exclusionSummaryError(error) });
        });
        return () => controller.abort();
    }, [key, enabled, refreshKey, reportId, configVersionId, artifactId, metricId, page, attempt]);
    // Hide the previous report/page immediately, before its effect runs.
    const data = cached.current?.key === key && cached.current.refreshKey === refreshKey ? cached.current.pages.get(page) || null : null;
    return { ...state, data, refresh: () => setAttempt(value => value + 1),
        prioritize: () => { if (metricId != null) prioritizeExclusionReasons({ reportId, configVersionId, artifactId }, metricId); } };
}
